import React from 'react';

import { sanitizeContent } from 'utilities/comments';

import { useGetWorkspaceTeamMembers } from 'hooks/workspace';

import MentionPill from '../mention-pill';
import {
  getSemanticCommentParts,
  Mention,
} from '../../../../../shared/utilities/mention';

const FILTERED_OUT_PROPS = [
  'workspaceTeamMembers',
  'withPills',
  'content',
  'forwardRef',
];
const MentionParser = props => {
  const {
    withPills,
    comment,
    beforeBlock,
    afterBlock,
    forwardRef: embedRef,
  } = props;
  const workspaceTeamMembers = useGetWorkspaceTeamMembers();

  /**
   *
   * @param {String} comment
   * @param {JSX.Element} beforeBlock
   * @param {JSX.Element} afterBlock
   * @returns {Array<React.ReactNode>}
   */
  const getSanitizedAndMentionedContent = (
    comment,
    beforeBlock,
    afterBlock
  ) => {
    const sContent = sanitizeContent(comment);

    /**
     *
     * @param {Mention} mention
     *  the mention instance from shared/utilities/mention.js
     * @returns {React.ReactNode}
     *  the mention pill or string for the given mention
     */
    const displayMention = (mention, idx) => {
      const { displayText, modelId } = mention;

      const teamMember =
        workspaceTeamMembers &&
        workspaceTeamMembers.find(
          member => String(member.id) === String(modelId)
        );

      const finalDisplayName = teamMember
        ? `@${teamMember.first_name} ${teamMember.last_name}`
        : displayText;

      return withPills ? (
        <MentionPill
          key={finalDisplayName + idx}
          teamMember={teamMember}
          name={finalDisplayName}
        />
      ) : (
        finalDisplayName
      );
    };

    /**
     *
     * @param {String} contentPart
     *  The raw content part in String format
     * @returns {React.ReactNode}
     *  A span that represents a react-ified version of the content part
     */
    const displayContent = (contentPart, beforeBlock, afterBlock, idx) => (
      <span key={`${contentPart}-${idx}`}>
        {beforeBlock}
        <span dangerouslySetInnerHTML={{ __html: contentPart }} />
        {afterBlock}
      </span>
    );

    const semanticParts = getSemanticCommentParts(sContent);

    return semanticParts.map((commentPart, idx) => {
      if (commentPart instanceof Mention) {
        return displayMention(commentPart, idx);
      }

      return displayContent(commentPart, beforeBlock, afterBlock, idx);
    });
  };

  const filteredProps = Object.keys(props)
    .filter(k => !FILTERED_OUT_PROPS.includes(k))
    .reduce((newProps, key) => ({ ...newProps, [key]: props[key] }), {});

  return (
    <div {...filteredProps} ref={embedRef}>
      {getSanitizedAndMentionedContent(comment, beforeBlock, afterBlock)}
    </div>
  );
};

export default MentionParser;
